import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Intent } from '@blueprintjs/core';
import { Button, ButtonSave } from 'components/buttons';
import { DataDisplayTable } from 'components/data-display';
import { DateToLocal } from 'components/dates';
import { FormDateInput, FormHelperText, FormSwitch, FormTextArea, FormTextInput } from 'components/form-fields';
import { GridLayout } from 'components/grid';
import { ShowHide } from 'components/layout';
import { AlertConfirm, NotificationInline, NotificationToaster } from 'components/notifications';
import { HelperFunctions } from 'helpers';
import { useValidation } from 'hooks/useValidation';
import {
    buildMedicalInformationRequest,
    buildMedicalValidationRules,
    showSaveFailureToast
} from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';
import { EmployeeService } from 'services';

const emptyFieldValue = "N/A";
const dateApiFormat = "YYYY-MM-DD";
const loadFailedInlineMessage = "The employee's medical information is currently unavailable. Refresh the page to try again.";
const refreshFailedInlineMessage = "The employee's medical information could not be checked for changes, so the values shown may be out of date. Refresh the page before editing them.";

// The backing columns are plain SQL DATE values, and an assessment can legitimately be
// years in the past. FormDateInput otherwise defaults to a narrow 30-months-back window
// and reports such dates as out of range.
const earliestSelectableDate = moment("1900-01-01").toDate();
const latestSelectableDate = moment("2100-01-01").toDate();

// An assessment records something that has already happened, and the endpoint rejects a
// future date. FormDateInput only raises onChange within its bounds, so the bound is what
// stops one being picked. Held at module scope because FormDateInput keys an effect off the
// maxDate reference.
const latestAssessmentDate = moment().endOf("day").toDate();

// The read-view field set and labels match the backend's MedicalInformationSnapshotBuilder
// (Employee.Services/EmployeeReview/MedicalInformationSnapshotBuilder.cs) so the wizard and
// the completed-review record present the same medical fields identically.
function buildMedicalFieldRows(medicalInformation) {
    return [
        {
            key: "last-health-assessment-date",
            header: "Last Health Assessment Date",
            value: <DateToLocal format="DD/MM/YYYY" invalidDateString={emptyFieldValue}>{medicalInformation?.lastHealthAssessmentDate}</DateToLocal>
        },
        {
            key: "last-eyesight-assessment-date",
            header: "Last Eyesight Assessment Date",
            value: <DateToLocal format="DD/MM/YYYY" invalidDateString={emptyFieldValue}>{medicalInformation?.lastEyesightAssessmentDate}</DateToLocal>
        },
        { key: "reportable-medical-condition", header: "Reportable Medical Condition", value: medicalInformation?.reportableMedicalCondition ? "Yes" : "No" },
        { key: "oh-referral", header: "Occupational-Health Referral", value: medicalInformation?.ohReferral || emptyFieldValue },
        {
            key: "oh-referral-date",
            header: "Occupational-Health Referral Date",
            value: <DateToLocal format="DD/MM/YYYY" invalidDateString={emptyFieldValue}>{medicalInformation?.ohReferralDate}</DateToLocal>
        }
    ];
}

// The Medical Information step of the complete-review wizard. Read mode shows the driver's
// recorded medical values; Edit mode lets the manager correct them. The values are special
// category under UK GDPR, so they are read and written through the employee's dedicated
// medical endpoints rather than the shared employee endpoints.
export function MedicalInformationStep(
    {
        state = null,
        employeeId = null,
        loading = false,
        onChange
    }
) {

    const [valid, errors, validate] = useValidation();
    const [showClearOccupationalHealthConfirm, setShowClearOccupationalHealthConfirm] = useState(false);

    const medicalInformation = state?.record ?? null;
    const draft = state?.draft ?? null;
    const editing = state?.editing === true;
    const saving = state?.saving === true;
    const loadFailed = state?.loadFailed === true;
    // The save sends the whole loaded record, so a record that could not be re-read is presented
    // as it stands but cannot be edited - saving it could revert a change made since it was read.
    const refreshFailed = state?.refreshFailed === true;

    const hasOccupationalHealthDetails = (draft?.ohReferral ?? "") !== "" || draft?.ohReferralDate != null;
    // The referral and its date only apply to a reportable condition, so they are asked for only
    // once one is recorded - matching the employee profile's Medical tab. Values already held
    // against no condition stay visible so the manager can see and clear what the endpoint rejects.
    const showOccupationalHealthFields = (draft?.reportableMedicalCondition ?? false) || hasOccupationalHealthDetails;

    // Mirror the medical endpoint's own rules so a rejected value is caught before the save.
    // None of the medical values are required, so an empty medical record is valid.
    useEffect(() => {
        if (!editing) {
            return;
        }

        validate([], {}, buildMedicalValidationRules(draft));
    }, [editing, draft]);

    function onDraftFieldChange(fieldName, value) {
        onChange({
            draft: {
                ...draft,
                [fieldName]: value
            }
        });
    }

    function onDraftDateFieldChange(fieldName, item) {
        onDraftFieldChange(fieldName, item ? moment(item).format(dateApiFormat) : null);
    }

    function onReportableMedicalConditionChange(reportable) {
        // Dropping the condition discards the occupational health details with it, so the manager
        // confirms before anything already recorded is lost.
        if (!reportable && draft?.reportableMedicalCondition === true && hasOccupationalHealthDetails) {
            setShowClearOccupationalHealthConfirm(true);
            return;
        }

        onDraftFieldChange("reportableMedicalCondition", reportable);
    }

    function onClearOccupationalHealthConfirm() {
        setShowClearOccupationalHealthConfirm(false);
        onChange({
            draft: {
                ...draft,
                reportableMedicalCondition: false,
                ohReferral: "",
                ohReferralDate: null
            }
        });
    }

    function onClearOccupationalHealthCancel() {
        setShowClearOccupationalHealthConfirm(false);
    }

    function onEdit() {
        onChange({ draft: HelperFunctions.deepClone(medicalInformation), editing: true });
    }

    function onCancel() {
        onChange({ draft: HelperFunctions.deepClone(medicalInformation), editing: false });
    }

    function onSave() {
        // Block the save while a value breaks one of the endpoint's rules; the inline error
        // is already shown against the field by the validation effect above.
        if (!valid) {
            return;
        }

        onChange({ saving: true });

        EmployeeService.saveMedicalInformation(employeeId, buildMedicalInformationRequest(draft)).then(function () {
            onChange({ record: HelperFunctions.deepClone(draft), editing: false, saving: false });
            NotificationToaster.show(Intent.SUCCESS, "Medical information has been successfully updated.");
        }, function (error) {
            onChange({ saving: false });
            showSaveFailureToast(error, "Failed to update the medical information. Please try again.");
        });
    }

    function onCommentChange(event) {
        onChange({ comment: event.target.value });
    }

    return (
        <div>
            <div className="inline-items inline-items-space spacer-bottom-small">
                <h2>Medical Information</h2>
                <div className="button-row">
                    <ShowHide
                        evaluator={!editing}
                        show={(
                            <Button id="medical-information-edit" icon="edit" text="Edit" loading={loading} disabled={saving || loading || loadFailed || refreshFailed} onClick={onEdit} />
                        )}
                        hide={(
                            <Fragment>
                                <Button id="medical-information-cancel" text="Cancel" loading={loading} disabled={saving} onClick={onCancel} />
                                <ButtonSave id="medical-information-save" loading={saving} disabled={saving} simpleDisabled={!valid || loading} onClick={onSave} />
                            </Fragment>
                        )}
                    />
                </div>
            </div>
            <ShowHide
                evaluator={loadFailed}
                show={(
                    <NotificationInline
                        allowClose={false}
                        show
                        text={loadFailedInlineMessage}
                        intent="danger"
                    />
                )}
                hide={(
                    <Fragment>
                        <ShowHide
                            evaluator={refreshFailed}
                            show={(
                                <NotificationInline
                                    allowClose={false}
                                    show
                                    text={refreshFailedInlineMessage}
                                    intent="warning"
                                />
                            )}
                        />
                        <FormHelperText loading={loading}>
                            {editing
                                ? "Update the employee's medical details."
                                : "Check the employee's medical details are correct and up to date."}
                        </FormHelperText>
                        <ShowHide
                            evaluator={!editing}
                            show={(
                                <DataDisplayTable data={buildMedicalFieldRows(medicalInformation)} loading={loading} />
                            )}
                            hide={(
                                <Fragment>
                                    <GridLayout numberOfColumns={2} gap={[0, 20]}>
                                        <FormDateInput
                                            id="medical-last-health-assessment-date"
                                            headingText="Last Health Assessment Date"
                                            value={draft?.lastHealthAssessmentDate}
                                            minDate={earliestSelectableDate}
                                            maxDate={latestAssessmentDate}
                                            loading={loading}
                                            disabled={saving}
                                            dangerHelperText={errors.lastHealthAssessmentDate}
                                            onChange={(item) => onDraftDateFieldChange("lastHealthAssessmentDate", item)}
                                        />
                                        <FormDateInput
                                            id="medical-last-eyesight-assessment-date"
                                            headingText="Last Eyesight Assessment Date"
                                            value={draft?.lastEyesightAssessmentDate}
                                            minDate={earliestSelectableDate}
                                            maxDate={latestAssessmentDate}
                                            loading={loading}
                                            disabled={saving}
                                            dangerHelperText={errors.lastEyesightAssessmentDate}
                                            onChange={(item) => onDraftDateFieldChange("lastEyesightAssessmentDate", item)}
                                        />
                                        <FormSwitch
                                            id="medical-reportable-medical-condition"
                                            headingText="Reportable Medical Condition"
                                            checked={draft?.reportableMedicalCondition ?? false}
                                            checkedLabel="Yes"
                                            uncheckedLabel="No"
                                            loading={loading}
                                            disabled={saving}
                                            onChange={(event) => onReportableMedicalConditionChange(event.target.checked)}
                                        />
                                    </GridLayout>
                                    <ShowHide
                                        evaluator={showOccupationalHealthFields}
                                        show={(
                                            <GridLayout numberOfColumns={2} gap={[0, 20]}>
                                                <FormTextInput
                                                    id="medical-oh-referral"
                                                    headingText="Occupational-Health Referral"
                                                    value={draft?.ohReferral ?? ""}
                                                    loading={loading}
                                                    disabled={saving}
                                                    dangerHelperText={errors.ohReferral}
                                                    onChange={(event) => onDraftFieldChange("ohReferral", event.target.value)}
                                                />
                                                <FormDateInput
                                                    id="medical-oh-referral-date"
                                                    headingText="Occupational-Health Referral Date"
                                                    value={draft?.ohReferralDate}
                                                    minDate={earliestSelectableDate}
                                                    maxDate={latestSelectableDate}
                                                    loading={loading}
                                                    disabled={saving}
                                                    dangerHelperText={errors.ohReferralDate}
                                                    onChange={(item) => onDraftDateFieldChange("ohReferralDate", item)}
                                                />
                                            </GridLayout>
                                        )}
                                    />
                                </Fragment>
                            )}
                        />
                    </Fragment>
                )}
            />
            <div className="spacer-top">
                <FormTextArea
                    id="medical-information-comment"
                    headingText="Overall comments"
                    placeholder="Add any overall comments for this topic..."
                    value={state?.comment ?? ""}
                    loading={loading}
                    disabled={saving}
                    fill
                    onChange={onCommentChange}
                />
            </div>
            <AlertConfirm
                title="Please confirm"
                isOpen={showClearOccupationalHealthConfirm}
                onConfirm={onClearOccupationalHealthConfirm}
                onCancel={onClearOccupationalHealthCancel}
            >
                <p>Removing the reportable medical condition will also remove the occupational health referral and its date. Are you sure you want to continue?</p>
            </AlertConfirm>
        </div>
    );
}

MedicalInformationStep.propTypes = {
    state: PropTypes.object,
    employeeId: PropTypes.number,
    loading: PropTypes.bool,
    onChange: PropTypes.func.isRequired
};
